'use client';

import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';

import clsx from 'clsx';

import { RevenueChart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/RevenueChart';
import { SalesChart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/SalesChart';
import { SalesCountComparisonChart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/SalesCountComparisonChart';
import { SalesVolumeComparisonChart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/SalesVolumeComparisonChart';
import { StatusChart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/StatusChart';
import { type GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { type AdaptiveContentTab, AdaptiveContentTabs } from '@/components/common/AdaptiveContentTabs';
import { Flex } from '@/components/common/Flex';
import { Spinner } from '@/components/common/Spinner';
import { useCurrency } from '@/hooks/use-currency';
import { type WithClassName } from '@/types/react';

import { StatusChartOperationType } from '../../../../../../../../../generated/graphql';

import styles from './Charts.module.scss';

type ChartsProps = WithClassName & {
  gateGroupDetails?: GateGroupDetails;
};

export const Charts: FC<ChartsProps> = (props) => {
  const t = useTranslations('pages.dashboard.widgets');

  const { className, gateGroupDetails } = props;

  const { data, loading } = useCurrency(true);

  const defaultCurrency = useMemo(() => {
    return {
      id: gateGroupDetails?.currency_id ?? data?.at(0)?.id ?? 'USD',
      name: '',
    };
  }, [data, gateGroupDetails?.currency_id]);

  const tabs = useMemo(
    (): AdaptiveContentTab<
      'sales' | 'revenue' | 'invoiceStatus' | 'withdrawStatus' | 'salesVolumeComparison' | 'invoiceCountComparison'
    >[] => [
      {
        key: 'sales',
        className: styles.sales,
        title: t('sales.title'),
        content: (
          <SalesChart title={t('sales.title')} defaultCurrency={defaultCurrency} gateGroupDetails={gateGroupDetails} />
        ),
      },
      {
        key: 'revenue',
        className: styles.revenue,
        title: t('revenue.title'),
        content: (
          <RevenueChart
            title={t('revenue.title')}
            defaultCurrency={defaultCurrency}
            gateGroupDetails={gateGroupDetails}
          />
        ),
      },
      {
        key: 'invoiceStatus',
        title: t('status.invoiceTitle'),
        content: (
          <StatusChart
            title={t('status.invoiceTitle')}
            defaultCurrency={defaultCurrency}
            operationType={StatusChartOperationType.Invoice}
            gateGroupDetails={gateGroupDetails}
          />
        ),
      },
      {
        key: 'withdrawStatus',
        title: t('status.withdrawTitle'),
        content: (
          <StatusChart
            title={t('status.withdrawTitle')}
            defaultCurrency={defaultCurrency}
            operationType={StatusChartOperationType.Withdraw}
            gateGroupDetails={gateGroupDetails}
          />
        ),
      },
      {
        key: 'salesVolumeComparison',
        title: t('salesVolumeComparison.title'),
        content: (
          <SalesVolumeComparisonChart
            title={t('salesVolumeComparison.title')}
            defaultCurrency={defaultCurrency}
            gateGroupDetails={gateGroupDetails}
          />
        ),
      },
      {
        key: 'invoiceCountComparison',
        title: t('salesCountComparison.title'),
        content: (
          <SalesCountComparisonChart title={t('salesCountComparison.title')} gateGroupDetails={gateGroupDetails} />
        ),
      },
    ],
    [defaultCurrency, gateGroupDetails, t]
  );

  return (
    <div className={clsx(styles.root, className)}>
      {loading && !gateGroupDetails ? (
        <Flex alignItems="center" justifyContent="center">
          <Spinner size={56} />
        </Flex>
      ) : (
        <AdaptiveContentTabs className={styles.widgets} tabs={tabs} />
      )}
    </div>
  );
};
